import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

import { ManagedLeadProductPath } from '../../../services/api/managed-leads/managedLeads';
import { selectUser } from '../../../store/user/userSelectors';
import { useUserStore } from '../../../store/user/useUserStore';
import ManagedDropReviewPanel from './components/ManagedDropReviewPanel';
import MortgageDropRequestsPanel from './components/MortgageDropRequestsPanel';

const MORTGAGE_TAB = 'mutui';

/**
 * The products served by Tasso Quinto, whose Drop requests go through the
 * administrative review. Mutui keeps its own panel and its own backend, so it
 * is not one of these.
 */
const MANAGED_TABS: {
  id: ManagedLeadProductPath;
  label: string;
  searchHelpText: string;
}[] = [
  {
    id: 'prestiti',
    label: 'Prestiti',
    searchHelpText: 'Almeno 2 caratteri: nome, email o telefono',
  },
  {
    id: 'cessioni-del-quinto',
    label: 'Cessioni del quinto',
    searchHelpText: 'Almeno 2 caratteri: nome o email',
  },
];

const readTab = (value: string | null) =>
  MANAGED_TABS.some((tab) => tab.id === value) ? value : MORTGAGE_TAB;

/**
 * The administrative Drop page. Mutui is the panel it has always been, and the
 * two products served by Tasso Quinto are separate tabs: their queue, filters
 * and decisions belong to another backend and must not reshape the workflow
 * Mutui already has.
 */
const DropRequestsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUserStore(selectUser);
  // The review scope is granted to admins alone. The backend enforces it on
  // every one of the three endpoints; the page reads the same rule so it does
  // not offer an action that could only come back refused.
  const canReview = user?.role === 'admin';
  const activeTab = readTab(searchParams.get('prodotto'));

  // Each queue has its own page, search and state filter: switching product
  // starts from the top rather than carrying over a view of another queue.
  const selectTab = (tab: string) =>
    setSearchParams(tab === MORTGAGE_TAB ? {} : { prodotto: tab });

  return (
    <Box>
      <Box px={4} pt={4} pb={2} gap={1} display="flex" alignItems="center">
        <DeleteSweepIcon fontSize="large" />
        <Typography variant="h4" fontWeight="bold">
          Richieste di drop
        </Typography>
      </Box>

      <Box px={4}>
        <Tabs
          value={activeTab}
          onChange={(_, tab) => selectTab(tab)}
          aria-label="Prodotto delle richieste di drop"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Mutui" value={MORTGAGE_TAB} id="drop-tab-mutui" />
          {MANAGED_TABS.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              value={tab.id}
              id={`drop-tab-${tab.id}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* One panel at a time: a hidden queue must not query a backend the admin
          is not looking at. */}
      <Box
        role="tabpanel"
        aria-labelledby={`drop-tab-${activeTab}`}
        px={4}
        py={3}
      >
        {activeTab === MORTGAGE_TAB ? (
          <MortgageDropRequestsPanel />
        ) : (
          MANAGED_TABS.filter((tab) => tab.id === activeTab).map((tab) => (
            <ManagedDropReviewPanel
              key={tab.id}
              product={tab.id}
              productName={tab.label}
              searchHelpText={tab.searchHelpText}
              canReview={canReview}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default DropRequestsPage;
